import { useRef, useState } from "react";
import { ApiError, uploadImage } from "../api";
import { compressImageFile, PhotoInputError, type CompressedImage } from "../photo/image";
import StatusMessage from "./StatusMessage";

type Status = "idle" | "preparing" | "uploading" | "error";

type Props = {
  onBack: () => void;
  onUploaded: (fileId: string, width: number, height: number, previewUrl: string) => void;
};

function errorMessage(err: unknown): string {
  if (err instanceof PhotoInputError) {
    if (err.reason === "too_large") return "Ảnh này khá lớn (trên 30MB) — bạn thử chọn ảnh khác nhé.";
    if (err.reason === "load_failed") return "Mình chưa đọc được ảnh này — thử chọn ảnh khác (JPG/PNG/WebP) nhé.";
    return "Có lỗi khi xử lý ảnh — bạn thử lại nhé.";
  }
  if (err instanceof ApiError) {
    if (err.status === 404) {
      return "Tính năng tải ảnh lên đang được hoàn thiện ở phía máy chủ — bạn quay lại thử sau ít phút nhé.";
    }
    if (err.message === "network") {
      return "Mạng đang chập chờn, mình chưa tải được ảnh lên. Thử lại giúp mình nhé.";
    }
    return "Có lỗi xảy ra khi tải ảnh lên. Thử lại giúp mình nhé.";
  }
  return "Có lỗi xảy ra. Thử lại giúp mình nhé.";
}

export default function PhotoUploadStep({ onBack, onUploaded }: Props) {
  const [status, setStatus] = useState<Status>("idle");
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [prepared, setPrepared] = useState<CompressedImage | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  function reset() {
    if (prepared) URL.revokeObjectURL(prepared.previewUrl);
    setPrepared(null);
    setStatus("idle");
    setProgress(0);
    setError(null);
    if (inputRef.current) inputRef.current.value = "";
  }

  async function startUpload(image: CompressedImage) {
    setStatus("uploading");
    setProgress(0);
    setError(null);
    try {
      const res = await uploadImage(image.blob, "photo.jpg", (pct) => setProgress(pct));
      onUploaded(res.fileId, res.width, res.height, image.previewUrl);
    } catch (err) {
      setStatus("error");
      setError(errorMessage(err));
    }
  }

  async function handleFile(file: File) {
    setStatus("preparing");
    setError(null);
    try {
      const image = await compressImageFile(file);
      setPrepared(image);
      void startUpload(image);
    } catch (err) {
      setStatus("error");
      setError(errorMessage(err));
    }
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) void handleFile(file);
  }

  const busy = status === "preparing" || status === "uploading";

  return (
    <section className="step photo-upload-step" aria-labelledby="step-photo-upload-title">
      <button type="button" className="link-back" onClick={onBack} disabled={busy}>
        ← Quay lại
      </button>

      <h1 id="step-photo-upload-title" className="step-title">
        Đưa QR vào ảnh của bạn
      </h1>
      <p className="hint-text small">
        Chụp hoặc chọn một tấm ảnh (bảng hiệu, tờ giấy, màn hình…) — mình sẽ giúp bạn tìm chỗ đặt mã QR đẹp
        nhất.
      </p>

      {prepared ? (
        <div className="photo-preview-frame">
          <img src={prepared.previewUrl} alt="Ảnh đã chọn" className="photo-preview-img" />
        </div>
      ) : (
        <label className="photo-picker" htmlFor="photo-file-input">
          <span className="photo-picker-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="36" height="36" fill="none" stroke="currentColor" strokeWidth="1.6">
              <path d="M4 8h3l1.5-2h7L17 8h3a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1Z" />
              <circle cx="12" cy="14" r="3.5" />
            </svg>
          </span>
          <span className="photo-picker-text">Chạm để chụp ảnh hoặc chọn từ thư viện</span>
        </label>
      )}

      <input
        ref={inputRef}
        id="photo-file-input"
        type="file"
        accept="image/jpeg,image/png,image/webp"
        capture="environment"
        className="photo-file-input"
        onChange={handleInputChange}
        disabled={busy}
      />

      {status === "preparing" ? <StatusMessage tone="loading" text="Đang chuẩn bị ảnh…" /> : null}

      {status === "uploading" ? (
        <div className="photo-progress" role="status">
          <div className="photo-progress-track">
            <div className="photo-progress-bar" style={{ width: `${progress}%` }} />
          </div>
          <p className="hint-text small">Đang tải ảnh lên… {progress}%</p>
        </div>
      ) : null}

      {status === "error" && error ? (
        <StatusMessage tone="error" text={error} onRetry={() => (prepared ? void startUpload(prepared) : reset())} />
      ) : null}

      {prepared ? (
        <button type="button" className="btn btn-secondary btn-block" onClick={reset} disabled={status === "uploading"}>
          Chọn ảnh khác
        </button>
      ) : null}
    </section>
  );
}
